import React, { useEffect, useMemo, useState } from "react";
import "./UserMessages.css";
import Table from "../../../../components/reusable-table/Table";
import Input from "../../../../components/reusable-input/Input";
import UserModal from "../user-modal/UserModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import { SearchIcon } from "../../../../icons/reactIcons";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import {
  getCmsMessageDetails,
  sendCmsMessage,
} from "../../../../redux/slices/cmsMessageSlice";
import { formatDateTime } from "../../../../utils/formatDateTime";

const columns = [
  { header: "Message", accessor: "message" },
  { header: "Created By", accessor: "createdBy" },
];

const ROWS_PER_PAGE = 30;

const UserMessages: React.FC = () => {
  const { userId, name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ✅ Get studyId from router state
  const studyId = location.state?.studyId || "";

  const { messageDetails, messageDetailsCount, detailsLoading, createLoading } =
    useAppSelector((state) => state.cmsMessages);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ server side pagination page
  const [page, setPage] = useState(1);

  // -----------------------------
  // FETCH MESSAGE DETAILS
  // -----------------------------
  useEffect(() => {
    if (!userId) return;

    dispatch(
      getCmsMessageDetails({
        userId: userId,
        pageId: page,
        limit: ROWS_PER_PAGE,
      }),
    );
  }, [dispatch, userId, page]);

  // -----------------------------
  // TABLE DATA
  // -----------------------------
  const formattedMessages = useMemo(() => {
    return (messageDetails || []).map((msg: any) => ({
      message: msg.messageDetail,

      createdBy: (
        <div className="merged-cell">
          <div className="merged-label">{msg.creator?.name || "-"}</div>

          <div className="merged-date">{formatDateTime(msg.createdAt)}</div>
        </div>
      ),
    }));
  }, [messageDetails]);

  // -----------------------------
  // SEARCH FILTER
  // -----------------------------
  const searchedData = useMemo(() => {
    if (!searchTerm.trim()) return formattedMessages;

    return formattedMessages.filter((item) =>
      item.message.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [formattedMessages, searchTerm]);

  const breadcrumbItems = [
    { label: "Users", onClick: () => navigate("/users") },
    {
      label: name ?? "",
      onClick: () => navigate(`/users/${name}/${userId}`),
    },
    { label: "Messages", disabled: true },
  ];

  // -----------------------------
  // SEND MESSAGE
  // -----------------------------
  const handleSendMessage = async (message: string) => {
    if (!userId) return;

    try {
      await dispatch(
        sendCmsMessage({
          message,
          users: [
            {
              id: String(userId),
              isActive: "true",
            },
          ],
        }),
      ).unwrap();

      // ✅ Refresh current page
      dispatch(
        getCmsMessageDetails({
          userId: userId,
          pageId: page,
          limit: ROWS_PER_PAGE,
        }),
      );

      setIsModalOpen(false);
    } catch (error) {
      console.error("Send message failed:", error);
    }
  };

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="user-messages-container">
        <div className="user-messages-header">
          <h2>Messages</h2>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1.5rem",
            }}
          >
            <Input
              type="text"
              placeholder="Search"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              search
              startIcon={<SearchIcon />}
              removeBottomMargin
            />

            <button
              className="user-messages-btn"
              onClick={() => setIsModalOpen(true)}
            >
              Send Message
            </button>
          </div>
        </div>

        <Table
          columns={columns}
          data={searchedData}
          rowsPerPage={ROWS_PER_PAGE}
          rounded
          loading={detailsLoading}
          serverSidePagination
          totalRecords={messageDetailsCount}
          onPageChange={(newPage) => setPage(newPage)}
        />

        {isModalOpen && (
          <UserModal
            type="message"
            name={name ?? ""}
            studyId={studyId}
            loading={createLoading}
            onClose={() => setIsModalOpen(false)}
            onSend={handleSendMessage}
          />
        )}
      </div>
    </>
  );
};

export default UserMessages;
